import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Input, Spin, Select, Empty, Avatar } from 'antd';
import { CloseOutlined, MessageOutlined, ArrowLeftOutlined, PlusOutlined } from '@ant-design/icons';
import toast from 'react-hot-toast';
import { useAuth } from '@/hooks/useAuth';
import * as chatApi from '@/services/api/internalChat.service';
import * as userService from '@/services/api/user.service';
import type { InternalConversation, InternalMessage } from '@/services/api/internalChat.service';
import './SupportChatWidget.css';

const POLL_INTERVAL_MS = 5000;

function messageText(m: InternalMessage): string {
  const t = m.content?.text;
  return typeof t === 'string' ? t : '';
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function timeAgo(iso: string | null | undefined): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

function initials(name?: string | null, email?: string | null): string {
  const n = (name || '').trim();
  if (n) {
    const parts = n.split(/\s+/);
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : n.slice(0, 2).toUpperCase();
  }
  return (email || '?').slice(0, 2).toUpperCase();
}

type View = 'list' | 'chat' | 'pick';

const SupportChatWidget = () => {
  const { user } = useAuth();
  const tenantId = user?.tenantId;
  const userId = user?.id;

  const [eligible, setEligible] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);

  const [view, setView] = useState<View>('list');
  const [conversations, setConversations] = useState<InternalConversation[]>([]);
  const [convsLoading, setConvsLoading] = useState(false);

  const [activeConv, setActiveConv] = useState<InternalConversation | null>(null);
  const [messages, setMessages] = useState<InternalMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  const [draft, setDraft] = useState('');
  const [sendLoading, setSendLoading] = useState(false);

  const [tenantUsers, setTenantUsers] = useState<{ id: string; label: string }[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(undefined);
  const [startingChat, setStartingChat] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (!tenantId) {
      setEligible(false);
      setBootstrapping(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setBootstrapping(true);
      try {
        const settings = await chatApi.getWidgetSettings();
        if (cancelled) return;
        setEligible(settings.chatWidgetEnabled && settings.chatWidgetEnabledForUsers);
      } catch {
        if (!cancelled) setEligible(false);
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId]);

  const loadConversations = useCallback(async () => {
    setConvsLoading(true);
    try {
      const res = await chatApi.listConversations({ limit: 50, offset: 0 });
      setConversations(res.conversations || []);
    } catch { /* ignore */ }
    setConvsLoading(false);
  }, []);

  useEffect(() => {
    if (panelOpen && eligible) loadConversations();
  }, [panelOpen, eligible, loadConversations]);

  const loadMessages = useCallback(async (convId: string, quiet?: boolean) => {
    if (!quiet) setMessagesLoading(true);
    try {
      const { messages: next } = await chatApi.getMessages(convId, { limit: 200, offset: 0 });
      setMessages(next);
    } catch {
      if (!quiet) toast.error('Failed to load messages');
    } finally {
      if (!quiet) setMessagesLoading(false);
    }
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  useEffect(() => {
    if (view !== 'chat' || !activeConv?.id) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    const id = activeConv.id;
    pollRef.current = setInterval(() => { void loadMessages(id, true); }, POLL_INTERVAL_MS);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [view, activeConv?.id, loadMessages]);

  const openConversation = async (conv: InternalConversation) => {
    setActiveConv(conv);
    setView('chat');
    setDraft('');
    await loadMessages(conv.id);
  };

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || !activeConv?.id) return;
    setSendLoading(true);
    try {
      await chatApi.sendMessage(activeConv.id, text);
      setDraft('');
      await loadMessages(activeConv.id, true);
    } catch {
      toast.error('Failed to send message');
    }
    setSendLoading(false);
  };

  const onDraftKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  const openUserPicker = async () => {
    setView('pick');
    setSelectedUserId(undefined);
    setUsersLoading(true);
    try {
      const res = await userService.listUsers({ page: 1, limit: 200 });
      const others = (res.users || [])
        .filter((u) => u.id !== userId)
        .map((u) => ({ id: u.id, label: u.full_name ? `${u.full_name} (${u.email})` : u.email }));
      setTenantUsers(others);
    } catch {
      toast.error('Failed to load users');
      setTenantUsers([]);
    }
    setUsersLoading(false);
  };

  const startChatWithUser = async () => {
    if (!selectedUserId) return;
    setStartingChat(true);
    try {
      const conv = await chatApi.createConversation({ participantId: selectedUserId });
      setActiveConv(conv);
      setView('chat');
      await loadMessages(conv.id);
    } catch {
      toast.error('Failed to start conversation');
    }
    setStartingChat(false);
  };

  const goBackToList = () => {
    setView('list');
    setActiveConv(null);
    setMessages([]);
    loadConversations();
  };

  const getOtherName = (conv: InternalConversation): string => {
    const isInitiator = conv.user_id === userId;
    if (isInitiator) {
      return conv.participant_full_name || conv.participant_email || 'Unknown';
    }
    return conv.initiator_full_name || conv.initiator_email || 'Unknown';
  };

  const getOtherInitials = (conv: InternalConversation): string => {
    const isInitiator = conv.user_id === userId;
    if (isInitiator) {
      return initials(conv.participant_full_name, conv.participant_email);
    }
    return initials(conv.initiator_full_name, conv.initiator_email);
  };

  const getOtherAvatarUrl = (conv: InternalConversation): string | undefined => {
    const isInitiator = conv.user_id === userId;
    return (isInitiator ? conv.participant_avatar_url : conv.initiator_avatar_url) || undefined;
  };

  if (!tenantId || bootstrapping || !eligible) return null;

  return (
    <>
      <button
        type="button"
        className="support-chat-widget__fab"
        aria-expanded={panelOpen}
        aria-label={panelOpen ? 'Close chat' : 'Open chat'}
        onClick={() => { setPanelOpen((o) => !o); if (!panelOpen) setView('list'); }}
      >
        <MessageOutlined className="support-chat-widget__fab-icon" />
      </button>

      <div
        className={`support-chat-widget__panel${panelOpen ? ' support-chat-widget__panel--open' : ''}`}
        role="dialog"
        aria-label="Team chat"
        aria-hidden={!panelOpen}
      >
        <div className="support-chat-widget__header">
          {view !== 'list' && (
            <button type="button" className="support-chat-widget__back" onClick={goBackToList}>
              <ArrowLeftOutlined />
            </button>
          )}
          <h2 className="support-chat-widget__title">
            {view === 'list' ? 'Team Chat' : view === 'pick' ? 'New Chat' : getOtherName(activeConv!)}
          </h2>
          <button type="button" className="support-chat-widget__close" onClick={() => setPanelOpen(false)}>
            <CloseOutlined />
          </button>
        </div>

        <div className="support-chat-widget__body">
          {view === 'list' && (
            <>
              <div className="support-chat-widget__list-actions">
                <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openUserPicker} block>
                  New Chat
                </Button>
              </div>
              {convsLoading ? (
                <div className="support-chat-widget__spin"><Spin /></div>
              ) : conversations.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No conversations yet" />
              ) : (
                <div className="support-chat-widget__conv-list">
                  {conversations.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className="support-chat-widget__conv-item"
                      onClick={() => void openConversation(c)}
                    >
                      <Avatar size={36} src={getOtherAvatarUrl(c)} className="support-chat-widget__conv-avatar">
                        {getOtherInitials(c)}
                      </Avatar>
                      <div className="support-chat-widget__conv-info">
                        <span className="support-chat-widget__conv-name">{getOtherName(c)}</span>
                        <span className="support-chat-widget__conv-time">
                          {timeAgo(c.last_message_at || c.updated_at)}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {view === 'pick' && (
            <div className="support-chat-widget__start">
              <p className="support-chat-widget__label">Select a team member to chat with</p>
              <Select
                showSearch
                placeholder="Search users..."
                style={{ width: '100%' }}
                loading={usersLoading}
                value={selectedUserId}
                onChange={setSelectedUserId}
                optionFilterProp="label"
                options={tenantUsers.map((u) => ({ value: u.id, label: u.label }))}
              />
              <Button
                type="primary"
                className="support-chat-widget__primary"
                loading={startingChat}
                disabled={!selectedUserId}
                onClick={() => void startChatWithUser()}
                style={{ marginTop: 12 }}
              >
                Start Chat
              </Button>
            </div>
          )}

          {view === 'chat' && activeConv && (
            <>
              {messagesLoading && messages.length === 0 ? (
                <div className="support-chat-widget__spin"><Spin /></div>
              ) : (
                <div className="support-chat-widget__messages">
                  {messages.length === 0 ? (
                    <p className="support-chat-widget__empty">No messages yet. Say hello below.</p>
                  ) : (
                    messages.map((m) => {
                      const isMine = m.sender_id === userId;
                      return (
                        <div
                          key={m.id}
                          className={`support-chat-widget__row${isMine ? ' support-chat-widget__row--user' : ' support-chat-widget__row--agent'}`}
                        >
                          <div
                            className={`support-chat-widget__bubble${isMine ? ' support-chat-widget__bubble--user' : ' support-chat-widget__bubble--agent'}`}
                          >
                            <div>{messageText(m)}</div>
                            <div className="support-chat-widget__meta">
                              {(m.sender_full_name || m.sender_email || (isMine ? 'You' : '')) +
                                ' · ' + formatTime(m.created_at)}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}

              <div className="support-chat-widget__composer">
                <div className="support-chat-widget__composer-row">
                  <Input.TextArea
                    className="support-chat-widget__textarea"
                    placeholder="Type a message…"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={onDraftKeyDown}
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    disabled={sendLoading}
                  />
                  <Button
                    type="primary"
                    className="support-chat-widget__send"
                    loading={sendLoading}
                    onClick={() => void handleSend()}
                  >
                    Send
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default SupportChatWidget;
